// /frontend/src/pages/legal/TermsOfService.jsx
import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, FileText, AlertTriangle, Scale, Ban, CreditCard, Gavel } from "lucide-react";

export default function TermsOfService() {
  const lastUpdated = "December 11, 2025";
  const effectiveDate = "December 11, 2025";

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#172A3A] via-[#1e3a4c] to-[#172A3A]">
      {/* Header */}
      <header className="bg-[#172A3A]/80 backdrop-blur-sm border-b border-white/10">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-white hover:text-[#FFB800] transition">
            <ArrowLeft size={20} />
            <span className="font-semibold">Back to Home</span>
          </Link>
          <Link to="/" className="flex items-center gap-2">
            <img src="/logo.png" alt="Pro IRP" className="w-8 h-8 rounded-full bg-white" />
            <span className="text-xl font-bold text-white">Pro <span className="text-[#FFB800]">IRP</span></span>
          </Link>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 py-12">
        <div className="bg-white rounded-2xl shadow-2xl p-8 md:p-12">
          {/* Title */}
          <div className="text-center mb-10">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-[#172A3A] rounded-full mb-4">
              <FileText className="w-8 h-8 text-[#FFB800]" />
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-[#172A3A] mb-2">Terms of Service</h1>
            <p className="text-gray-500">Last Updated: {lastUpdated}</p>
            <p className="text-gray-500">Effective Date: {effectiveDate}</p>
          </div>

          {/* Introduction */}
          <section className="mb-10">
            <p className="text-gray-700 leading-relaxed">
              Welcome to Pro IRP. These Terms of Service ("Terms") govern your access to and use of the Pro IRP
              platform, website, and services (collectively, the "Service"). By accessing or using the Service, you
              agree to be bound by these Terms. If you do not agree to these Terms, do not use the Service.
            </p>
          </section>

          {/* Blue Button Attribution - REQUIRED BY CMS */}
          <section className="mb-10 bg-blue-50 rounded-xl p-6 border border-blue-200">
            <div className="flex items-start gap-4">
              <FileText className="w-6 h-6 text-blue-600 mt-1 flex-shrink-0" />
              <div>
                <h2 className="text-xl font-bold text-[#172A3A] mb-2">CMS Blue Button 2.0 Integration Notice</h2>
                <p className="text-gray-700 font-medium">
                  This product uses the Blue Button 2.0 APIs but is not endorsed or certified by the Centers for
                  Medicare & Medicaid Services or the U.S. Department of Health and Human Services.
                </p>
              </div>
            </div>
          </section>

          {/* Agreement Notice */}
          <section className="mb-10 bg-amber-50 rounded-xl p-6 border border-amber-100">
            <div className="flex items-start gap-4">
              <AlertTriangle className="w-6 h-6 text-amber-600 mt-1 flex-shrink-0" />
              <div>
                <h2 className="text-xl font-bold text-[#172A3A] mb-2">Important Notice</h2>
                <p className="text-gray-700">
                  These Terms contain an arbitration agreement and class action waiver that affect your legal rights.
                  Please read them carefully. By using our Service, you agree to resolve disputes through binding
                  individual arbitration and waive your right to participate in class actions.
                </p>
              </div>
            </div>
          </section>

          {/* Privacy Policy Reference */}
          <section className="mb-10 bg-green-50 rounded-xl p-6 border border-green-100">
            <div className="flex items-start gap-4">
              <Scale className="w-6 h-6 text-green-600 mt-1 flex-shrink-0" />
              <div>
                <h2 className="text-xl font-bold text-[#172A3A] mb-2">Privacy Policy</h2>
                <p className="text-gray-700">
                  Your use of the Service is also governed by our <Link to="/privacy" className="text-[#FFB800] hover:underline font-semibold">Privacy Policy</Link> (available at proirp.com/privacy),
                  which is incorporated into these Terms by reference. Nothing in these Terms of Service shall contradict,
                  negate, or detract from the privacy protections detailed in our Privacy Policy. In the event of any
                  conflict between these Terms and the Privacy Policy regarding data protection, the Privacy Policy shall prevail.
                </p>
              </div>
            </div>
          </section>

          {/* Definitions */}
          <section className="mb-10">
            <h2 className="text-2xl font-bold text-[#172A3A] mb-4">1. Definitions</h2>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li><strong>"Pro IRP," "we," "us," or "our"</strong> refers to Pro IRP and its affiliates.</li>
              <li><strong>"User," "you," or "your"</strong> refers to any individual or entity using the Service.</li>
              <li><strong>"Agent"</strong> refers to licensed insurance agents using the Service.</li>
              <li><strong>"Client"</strong> refers to insurance clients/beneficiaries whose data is managed in the Service.</li>
              <li><strong>"PHI"</strong> refers to Protected Health Information as defined under HIPAA.</li>
              <li><strong>"Content"</strong> refers to all data, text, information, and materials uploaded or entered into the Service.</li>
            </ul>
          </section>

          {/* Eligibility */}
          <section className="mb-10">
            <h2 className="text-2xl font-bold text-[#172A3A] mb-4">2. Eligibility</h2>
            <p className="text-gray-700 mb-4">To use our Service, you must:</p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li>Be at least 18 years of age</li>
              <li>Be a licensed insurance agent, agency, or authorized representative (for agent accounts)</li>
              <li>Have the legal authority to enter into these Terms</li>
              <li>Not be prohibited from using the Service under applicable laws</li>
              <li>Maintain valid insurance licenses in your operating jurisdictions</li>
            </ul>
          </section>

          {/* Account Responsibilities */}
          <section className="mb-10">
            <h2 className="text-2xl font-bold text-[#172A3A] mb-4">3. Account Registration and Security</h2>
            <p className="text-gray-700 mb-4">When you create an account, you agree to:</p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li>Provide accurate, current, and complete information</li>
              <li>Maintain and promptly update your account information</li>
              <li>Keep your login credentials confidential and secure</li>
              <li>Immediately notify us of any unauthorized access to your account</li>
              <li>Accept responsibility for all activities that occur under your account</li>
              <li>Use strong passwords and enable two-factor authentication when available</li>
            </ul>
            <p className="text-gray-700 mt-4">
              You are solely responsible for any activity conducted through your account. Pro IRP is not liable for
              any loss or damage arising from unauthorized use of your account.
            </p>
          </section>

          {/* Acceptable Use */}
          <section className="mb-10">
            <div className="flex items-center gap-3 mb-4">
              <Scale className="w-6 h-6 text-[#FFB800]" />
              <h2 className="text-2xl font-bold text-[#172A3A]">4. Acceptable Use</h2>
            </div>
            <p className="text-gray-700 mb-4">You agree to use the Service only for lawful purposes and in compliance with:</p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li>All applicable federal, state, and local laws and regulations</li>
              <li>HIPAA and other healthcare privacy regulations</li>
              <li>The Privacy Act of 1974</li>
              <li>CMS Medicare Marketing Guidelines</li>
              <li>State insurance regulations and licensing requirements</li>
              <li>The CMS Blue Button 2.0 Terms of Service (for Medicare data access)</li>
            </ul>
          </section>

          {/* Prohibited Activities */}
          <section className="mb-10">
            <div className="flex items-center gap-3 mb-4">
              <Ban className="w-6 h-6 text-red-500" />
              <h2 className="text-2xl font-bold text-[#172A3A]">5. Prohibited Activities</h2>
            </div>
            <p className="text-gray-700 mb-4">You agree NOT to:</p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li>Access or use PHI for any purpose not authorized by the beneficiary</li>
              <li>Share, sell, or disclose client data to unauthorized third parties</li>
              <li>Request, access, use, or share a Medicare beneficiary's Medicare.gov login credentials</li>
              <li>Use the Service for any fraudulent, deceptive, or illegal activity</li>
              <li>Violate Medicare marketing guidelines or engage in prohibited marketing practices</li>
              <li>Attempt to gain unauthorized access to any portion of the Service</li>
              <li>Reverse engineer, decompile, or disassemble the Service</li>
              <li>Introduce malware, viruses, or other harmful code</li>
              <li>Use automated systems to access the Service without permission</li>
              <li>Interfere with or disrupt the Service or servers</li>
              <li>Impersonate any person or entity</li>
              <li>Harvest or collect user information without consent</li>
              <li>Use the Service to compete with Pro IRP</li>
            </ul>
          </section>

          {/* Medicare Data / Blue Button */}
          <section className="mb-10 bg-blue-50 rounded-xl p-6 border border-blue-100">
            <h2 className="text-xl font-bold text-[#172A3A] mb-4">6. Medicare Data and Blue Button 2.0</h2>
            <p className="text-gray-700 mb-4">
              When using the CMS Blue Button 2.0 integration to access Medicare beneficiary data, you additionally agree to:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li>Only access data with explicit beneficiary authorization obtained through Medicare.gov</li>
              <li>Use Medicare data solely for the purpose of serving the beneficiary's insurance needs</li>
              <li>Not use Medicare data for marketing purposes beyond the authorized scope</li>
              <li>Comply with all CMS Blue Button 2.0 Terms of Service and policies</li>
              <li>Promptly honor beneficiary requests to revoke data access</li>
              <li>Report any suspected data breaches involving Medicare data immediately</li>
              <li>Never request or access a beneficiary's Medicare.gov login credentials</li>
            </ul>
          </section>

          {/* HIPAA and PHI */}
          <section className="mb-10">
            <h2 className="text-2xl font-bold text-[#172A3A] mb-4">7. HIPAA Compliance and PHI</h2>
            <p className="text-gray-700 mb-4">
              As a user handling Protected Health Information (PHI), you acknowledge and agree that:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li>You will maintain appropriate safeguards to protect PHI</li>
              <li>You will only access PHI as necessary for your authorized purposes</li>
              <li>You will report any unauthorized access or disclosure of PHI immediately</li>
              <li>You will cooperate with Pro IRP in any breach investigation</li>
              <li>You may be required to execute a Business Associate Agreement (BAA)</li>
              <li>You will train any staff who access the Service on HIPAA requirements</li>
            </ul>
          </section>

          {/* Subscription and Payment */}
          <section className="mb-10">
            <div className="flex items-center gap-3 mb-4">
              <CreditCard className="w-6 h-6 text-[#FFB800]" />
              <h2 className="text-2xl font-bold text-[#172A3A]">8. Subscription and Payment</h2>
            </div>
            <h3 className="text-lg font-semibold text-[#172A3A] mt-4 mb-2">8.1 Fees</h3>
            <p className="text-gray-700 mb-4">
              Access to certain features of the Service requires a paid subscription. Fees are as described on our
              pricing page and are subject to change with 30 days' notice.
            </p>

            <h3 className="text-lg font-semibold text-[#172A3A] mt-4 mb-2">8.2 Billing</h3>
            <p className="text-gray-700 mb-4">
              Subscriptions are billed in advance on a monthly or annual basis. You authorize us to charge your
              provided payment method for all fees incurred.
            </p>

            <h3 className="text-lg font-semibold text-[#172A3A] mt-4 mb-2">8.3 Cancellation</h3>
            <p className="text-gray-700 mb-4">
              You may cancel your subscription at any time. Cancellation takes effect at the end of the current
              billing period. No refunds are provided for partial periods.
            </p>

            <h3 className="text-lg font-semibold text-[#172A3A] mt-4 mb-2">8.4 Free Trial</h3>
            <p className="text-gray-700">
              We may offer free trials. At the end of a free trial, your account will be charged unless you cancel
              before the trial ends.
            </p>
          </section>

          {/* Intellectual Property */}
          <section className="mb-10">
            <h2 className="text-2xl font-bold text-[#172A3A] mb-4">9. Intellectual Property</h2>
            <h3 className="text-lg font-semibold text-[#172A3A] mt-4 mb-2">9.1 Our Property</h3>
            <p className="text-gray-700 mb-4">
              The Service, including all software, designs, text, graphics, and other content created by Pro IRP,
              is owned by Pro IRP and protected by intellectual property laws. You may not copy, modify, distribute,
              or create derivative works without our written permission.
            </p>

            <h3 className="text-lg font-semibold text-[#172A3A] mt-4 mb-2">9.2 Your Content</h3>
            <p className="text-gray-700">
              You retain ownership of content you upload to the Service. By uploading content, you grant Pro IRP a
              limited license to use, store, and process that content solely to provide the Service to you.
            </p>
          </section>

          {/* Disclaimers */}
          <section className="mb-10">
            <h2 className="text-2xl font-bold text-[#172A3A] mb-4">10. Disclaimers</h2>
            <div className="bg-gray-50 rounded-lg p-4 text-gray-700">
              <p className="mb-4">
                <strong>THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR
                IMPLIED, INCLUDING BUT NOT LIMITED TO WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE,
                AND NON-INFRINGEMENT.</strong>
              </p>
              <p className="mb-4">
                Pro IRP does not warrant that the Service will be uninterrupted, error-free, or secure. We do not
                provide insurance advice or make recommendations about insurance products.
              </p>
              <p>
                Pro IRP is not responsible for the accuracy of data retrieved from third-party sources, including
                CMS Blue Button 2.0.
              </p>
            </div>
          </section>

          {/* Limitation of Liability */}
          <section className="mb-10">
            <h2 className="text-2xl font-bold text-[#172A3A] mb-4">11. Limitation of Liability</h2>
            <div className="bg-gray-50 rounded-lg p-4 text-gray-700">
              <p className="mb-4">
                <strong>TO THE MAXIMUM EXTENT PERMITTED BY LAW, PRO IRP SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL,
                SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS OR REVENUES, WHETHER INCURRED
                DIRECTLY OR INDIRECTLY.</strong>
              </p>
              <p>
                Our total liability for any claims arising from or related to these Terms or the Service shall not
                exceed the amount you paid to Pro IRP in the twelve (12) months preceding the claim.
              </p>
            </div>
          </section>

          {/* Indemnification */}
          <section className="mb-10">
            <h2 className="text-2xl font-bold text-[#172A3A] mb-4">12. Indemnification</h2>
            <p className="text-gray-700">
              You agree to indemnify, defend, and hold harmless Pro IRP and its officers, directors, employees, and
              agents from any claims, damages, losses, liabilities, and expenses (including attorneys' fees) arising
              from: (a) your use of the Service; (b) your violation of these Terms; (c) your violation of any law or
              regulation; (d) your violation of any third party's rights; or (e) any unauthorized access to PHI
              resulting from your actions or omissions.
            </p>
          </section>

          {/* Termination */}
          <section className="mb-10">
            <h2 className="text-2xl font-bold text-[#172A3A] mb-4">13. Termination</h2>
            <p className="text-gray-700 mb-4">
              We may suspend or terminate your access to the Service at any time, with or without cause, with or
              without notice. Grounds for termination include:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li>Violation of these Terms</li>
              <li>Non-payment of fees</li>
              <li>Fraudulent or illegal activity</li>
              <li>Loss of required licenses</li>
              <li>Actions that harm Pro IRP or other users</li>
            </ul>
            <p className="text-gray-700 mt-4">
              Upon termination, your right to use the Service ceases immediately. Provisions that by their nature
              should survive termination will survive, including intellectual property, disclaimers, limitations of
              liability, and indemnification.
            </p>
          </section>

          {/* Arbitration */}
          <section className="mb-10">
            <div className="flex items-center gap-3 mb-4">
              <Gavel className="w-6 h-6 text-[#FFB800]" />
              <h2 className="text-2xl font-bold text-[#172A3A]">14. Dispute Resolution</h2>
            </div>
            <h3 className="text-lg font-semibold text-[#172A3A] mt-4 mb-2">14.1 Informal Resolution</h3>
            <p className="text-gray-700 mb-4">
              Before filing a formal dispute, you agree to contact us at fluegel.scott@proirp.com to attempt informal resolution.
            </p>

            <h3 className="text-lg font-semibold text-[#172A3A] mt-4 mb-2">14.2 Binding Arbitration</h3>
            <p className="text-gray-700 mb-4">
              Any dispute not resolved informally shall be resolved by binding arbitration under the rules of the
              American Arbitration Association. Arbitration will be conducted in the Commonwealth of Kentucky.
            </p>

            <h3 className="text-lg font-semibold text-[#172A3A] mt-4 mb-2">14.3 Class Action Waiver</h3>
            <p className="text-gray-700">
              You agree to resolve disputes only on an individual basis and waive any right to participate in class
              actions or class arbitrations.
            </p>
          </section>

          {/* Governing Law */}
          <section className="mb-10">
            <h2 className="text-2xl font-bold text-[#172A3A] mb-4">15. Governing Law</h2>
            <p className="text-gray-700">
              These Terms shall be governed by and construed in accordance with the laws of the Commonwealth of Kentucky,
              without regard to conflict of law principles. Any legal action not subject to arbitration shall be
              brought exclusively in the state or federal courts located in Kentucky.
            </p>
          </section>

          {/* Changes */}
          <section className="mb-10">
            <h2 className="text-2xl font-bold text-[#172A3A] mb-4">16. Changes to Terms</h2>
            <p className="text-gray-700">
              We may modify these Terms at any time. We will notify you of material changes by posting the updated
              Terms on our website and updating the "Last Updated" date. Your continued use of the Service after
              changes take effect constitutes acceptance of the modified Terms.
            </p>
          </section>

          {/* Miscellaneous */}
          <section className="mb-10">
            <h2 className="text-2xl font-bold text-[#172A3A] mb-4">17. Miscellaneous</h2>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li><strong>Entire Agreement:</strong> These Terms constitute the entire agreement between you and Pro IRP.</li>
              <li><strong>Severability:</strong> If any provision is found unenforceable, the remaining provisions remain in effect.</li>
              <li><strong>Waiver:</strong> Our failure to enforce any right does not waive that right.</li>
              <li><strong>Assignment:</strong> You may not assign these Terms without our consent. We may assign our rights freely.</li>
              <li><strong>Notices:</strong> We may provide notices via email, the Service, or posting on our website.</li>
            </ul>
          </section>

          {/* Contact */}
          <section className="bg-gray-50 rounded-xl p-6 border border-gray-200">
            <h2 className="text-xl font-bold text-[#172A3A] mb-4">Contact Us</h2>
            <p className="text-gray-700 mb-4">
              If you have questions about these Terms, please contact us:
            </p>
            <div className="text-gray-700 space-y-1">
              <p><strong>Pro IRP</strong></p>
              <p>Email: <a href="mailto:fluegel.scott@proirp.com" className="text-[#FFB800] hover:underline">fluegel.scott@proirp.com</a></p>
              <p>Website: <a href="https://proirp.com" className="text-[#FFB800] hover:underline">proirp.com</a></p>
            </div>
          </section>

          {/* Footer Links */}
          <div className="mt-10 pt-6 border-t border-gray-200 flex flex-wrap gap-4 justify-center text-sm">
            <Link to="/privacy" className="text-[#FFB800] hover:underline">Privacy Policy</Link>
            <span className="text-gray-300">|</span>
            <Link to="/" className="text-gray-500 hover:text-[#172A3A]">Home</Link>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-8 text-center text-white/60 text-sm">
        <p>&copy; {new Date().getFullYear()} Pro IRP. All rights reserved.</p>
      </footer>
    </div>
  );
}
